import { useEffect } from "react";

/**
 * Custom hook to track page views for Google Analytics / GTM on route change.
 *
 * NOTE: This hook no longer sets document.title. The page title is owned solely by
 * <SEOMetadata /> (react-helmet + a synchronous document.title write), which pulls
 * the unique, SEO-approved title from seoConfig. Having this hook also write the
 * title caused a race — e.g. /us/1000EFI showed the SKU "TU-1000EFI-2 | Tuatara"
 * instead of the configured "1000 EFI Petrol UTV | Tuatara ATV USA". We now only
 * read the (already-set) document.title for analytics.
 *
 * @param {string} _title - Deprecated; retained for call-site compatibility. Ignored.
 * @param {string} _suffix - Deprecated; retained for call-site compatibility. Ignored.
 */
const usePageTitle = (_title, _suffix = "Tuatara") => {
  useEffect(() => {
    // Small delay so SEOMetadata has applied the correct title before we read it.
    const timer = setTimeout(() => {
      // Track page view with Google Analytics (gtag) if available
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "page_view", {
          page_title: document.title,
          page_location: window.location.href,
          page_path: window.location.pathname,
        });

        // Also send a custom event to ensure tracking
        window.gtag("event", "page_title_updated", {
          event_category: "Navigation",
          event_label: document.title,
          page_title: document.title,
        });
      }

      // Track page view with Google Tag Manager if available
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "page_view",
          page_title: document.title,
          page_location: window.location.href,
          page_path: window.location.pathname,
        });

        // Also push a custom event for title updates
        window.dataLayer.push({
          event: "page_title_updated",
          page_title: document.title,
          page_location: window.location.href,
          page_path: window.location.pathname,
        });
      }

      // Debug logging (remove in production)
      console.log("Page title updated:", document.title);
      console.log("Current path:", window.location.pathname);
    }, 100);

    // Cleanup function
    return () => {
      clearTimeout(timer);
    };
  }, [_title]);
};

export default usePageTitle;
